<template>
  <section class="ma-4 ma-md-8">
    <t-page-title
      v-if="can('create', 'banners')"
      title="Banners | Ordenar"
      :on-add="() => router.push({ name: 'create-banner' })"
    />
    <t-page-title v-else title="Banners | Ordenar" />
    <v-card class="pa-6">
      <v-card-text>
        <vue-draggable-next
          tag="div"
          :list="banners"
          class="list-group"
          handle=".handle"
          item-key="id"
        >
          <v-card
            v-for="banner, index in banners"
            :key="index"
            class="mx-auto ma-3"
            elevation="2"
          >
            <div class="d-flex align-center justify-center ma-1">
              <div class="pa-3 text-h5">
                {{ index + 1 }}
              </div>
              <v-divider vertical />
              <div class="pa-3">
                <a :href="banner.link" target="_blank">
                  <v-img :src="banner.url" :width="dimensions[banner.platform].width" :height="dimensions[banner.platform].height" />
                </a>
              </div>
              <div class="d-flex flex-column w-100">
                <div class="mb-2">
                  <div class="d-flex align-center">
                    <v-icon
                      class="mr-4"
                      :icon="banner.platform === Platforms.DESKTOP ? 'fa-regular fa-desktop' : 'fa-regular fa-mobile'"
                    />
                    <div class="text-h6">
                      {{ banner.title }}
                    </div>
                  </div>
                  <div class="text-caption">
                    {{ banner.description }}
                  </div>
                </div>
                <div>
                  <div class="text-subtitle">
                    <strong>Produtor:</strong> <span>{{ banner.producer?.trade_name ?? 'Todos' }}</span>
                  </div>
                  <div class="text-subtitle">
                    <strong>Publicação: </strong>
                    <span>{{ fromSQL(banner.publish_at) }}</span> -
                    <span>{{ fromSQL(banner.unpublish_at) }}</span>
                  </div>
                </div>
              </div>
              <v-icon
                class="handle"
                style="cursor: pointer"
                start
                size="x-large"
                color="grey-darken-1"
                icon="fa-regular fa-sort"
              />
            </div>
          </v-card>
        </vue-draggable-next>
      </v-card-text>
      <v-row v-if="can('sort', 'banners')" justify="end">
        <v-col cols="12" md="3">
          <v-btn
            color="primary"
            class="w-100"
            :loading="loader.loading"
            :disabled="loader.loading"
            @click="submit"
          >
            Salvar
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
  </section>
</template>

<script lang="ts" setup>

import TPageTitle from '@/components/TPageTitle.vue'
import { useSnackBar } from '@/stores/snackbar'
import { useLoader } from '@/stores/loader'
import { useAbility } from '@casl/vue'
import { useRouter } from 'vue-router'
import BannerService from '@/services/BannerService'
import { onMounted, ref } from 'vue'
import { Banner } from '@/types/Banner'
import { VueDraggableNext } from 'vue-draggable-next'
import { PlatformDimensions, Platforms } from '@/constants/Banners'
import { fromSQL } from '@/plugins/date'

const { can } = useAbility()
const router = useRouter()
const loader = useLoader()
const snackbar = useSnackBar()

// Preview image dimensions
const dimensions = {} as typeof PlatformDimensions

const heights: Record<Platforms, number> = { desktop: 100, mobile: 100 }

for (const p of Object.values(Platforms)) {
  dimensions[p] = {
    width: heights[p] * PlatformDimensions[p].width / PlatformDimensions[p].height,
    height: heights[p],
  }
}

// Banners
const banners = ref<Banner[]>([])

async function fetch() {
  try {
    loader.increment()
    const response = await (new BannerService()).index(1, 'status=true', 100)
    banners.value = response.data.data.sort((a, b) => a.sorting_order - b.sorting_order)

    if (banners.value.length === 0) {
      snackbar.show('Nenhum banner disponível no momento.', 'info')
    }
  } finally {
    loader.decrement()
  }
}

async function submit() {
  try {
    loader.increment()

    await (new BannerService()).updateSortingOrder(banners.value.map(b => b.id))

    snackbar.show('Banners reordenados com sucesso!', 'success')
  } finally {
    loader.decrement()
  }
}

onMounted(fetch)

</script>
